import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Card
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

function AdminContactUs() {

  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [complaints, setComplaints] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);


    //If current User is not admin => SIGN THE OUT !!
    useEffect(() => {
      const verifyAdmin = async () => {
        try {
          const user = auth.currentUser;
  
          // If no user is signed in, do nothing
          if (!user) return;
  
          // Query the Admin collection to find the user by UID
          const adminQuery = query(
            collection(db, 'admin'),
            where('uid', '==', user.uid)
          );
  
          const querySnapshot = await getDocs(adminQuery);
  
          if (querySnapshot.empty) {
            console.warn('User is not an admin. Signing out...');
            
            // Sign out the user
            await signOut(auth);
          }
        } catch (err) {
          console.error('Error verifying admin access:', err);
          await signOut(auth); // Sign out for safety
        }
      };
  
      verifyAdmin();
    }, []); 


  useEffect(() => {
    const fetchComplaints = async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser?.uid) {
          await setDoc(
            doc(db, 'admin', currentUser.uid),
            { lastSeenContactUsAt: serverTimestamp() },
            { merge: true }
          );
        }

        const complaintsSnapshot = await getDocs(collection(db, 'contactUs'));
        const complaintsList = complaintsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComplaints(complaintsList);
      } catch (error) {
        console.error('Error fetching complaints:', error);
      }
    };

    fetchComplaints();
  }, []);

  const updateComplaintStatus = async (complaintId, isSolved) => {
    try {
      const complaintRef = doc(db, "contactUs", complaintId);
      await updateDoc(complaintRef, { isSolved });
      console.log(`Complaint ${complaintId} updated to ${isSolved ? "solved" : "unsolved"}.`);
    } catch (error) {
      console.error("Error updating complaint status:", error);
      setError(t("حدث خطأ أثناء تحديث حالة الشكوى. يرجى المحاولة مرة أخرى"));
    }
  };

  const handleDeleteClick = (complaint) => {
    setSelectedComplaint(complaint);
    setOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedComplaint) {
      try {
        await deleteDoc(doc(db, 'contactUs', selectedComplaint.id));
        setComplaints((prev) =>
          prev.filter((complaint) => complaint.id !== selectedComplaint.id)
        );
        console.log(`Complaint ${selectedComplaint.id} deleted successfully.`);
      } catch (error) {
        console.error("Error deleting complaint:", error);
        setError(t("حدث خطأ أثناء حذف الشكوى. يرجى المحاولة مرة أخرى"));
      }
    }
    setOpen(false);
    setSelectedComplaint(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedComplaint(null);
  };

return (
  <>
    {/* Top bar (same as your other admin pages) */}
    <AdminTopBar />

    {/* Layout below the fixed AppBar */}
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)",
        mt: "64px",
        backgroundColor: "#fdfdfdff",
        flexDirection: "row-reverse", // sidebar on RIGHT
        overflow: "hidden",
      }}
    >
      {/* RIGHT: Sidebar */}
      <AdminNavbar />

      {/* LEFT: Main content (scrollable) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, md: 2 },
          overflowY: "auto",
        }}
      >
        {/* Header card (same style as Students page) */}
        <Card
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 4,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          {/* LEFT: icon */}
          <Box></Box>

          {/* RIGHT: title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t("صفحة الشكاوى والاستفسارات")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("إدارة الرسائل، تحديث الحالة، والحذف")}
                </Typography>
              </Box>
              
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <TextsmsOutlinedIcon />
              </Avatar>
          </Box>
        </Card>

        {/* Content card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 1.5, md: 2.5 },
            backgroundColor: "#ffffff",
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.06)",
            direction: "rtl",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              textAlign: "right",
              mb: 2,
            }}
          >
            {t("قائمة الشكاوى والاتصالات")}
          </Typography>

          {error && (
            <Typography sx={{ color: "error.main", mb: 2, textAlign: "right" }}>
              {error}
            </Typography>
          )}

          {complaints.length === 0 ? (
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                py: 4,
              }}
              >
              {t("لا توجد شكاوى حاليا")}
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Table sx={{ direction: "rtl" }}>
                <TableHead sx={{ bgcolor: "#f9fafb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>
                      {t("الاسم")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>
                      {t("رقم الهاتف")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>
                      {t("الرسالة")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>
                      {t("تم حل المشكل")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {complaints.map((complaint) => (
                    <TableRow
                      key={complaint.id}
                      hover
                      sx={{
                        "& td": { py: 2 },
                        "&:hover": { backgroundColor: "#fafafa" },
                      }}
                    >
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexDirection: "row-reverse",
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>
                              {`${complaint.firstName} ${complaint.lastName}`}
                            </Typography>
                          </Box>

                          <Avatar
                            sx={{
                              bgcolor: "rgba(193,130,130,0.18)",
                              color: "#8b3a3a",
                              width: 42,
                              height: 42,
                            }}
                          >
                            {(complaint.firstName?.[0] || t("؟")).toUpperCase()}
                          </Avatar>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ textAlign: "right" }}>
                        {complaint.phone || t("-")}
                      </TableCell>

                      <TableCell sx={{ textAlign: "right" }}>
                        {complaint.message || t("-")}
                      </TableCell>

                      <TableCell sx={{ textAlign: "right" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexDirection: "row-reverse",
                          }}
                        >
                          <Switch
                            checked={complaint.isSolved || false}
                            onChange={async () => {
                              const newSolvedStatus = !complaint.isSolved;
                              await updateComplaintStatus(
                                complaint.id,
                                newSolvedStatus
                              );
                              setComplaints((prev) =>
                                prev.map((item) =>
                                  item.id === complaint.id
                                    ? { ...item, isSolved: newSolvedStatus }
                                    : item
                                )
                              );
                            }}
                            color="success"
                          />

                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(complaint)}
                            sx={{
                              borderRadius: 2,
                              "&:hover": { backgroundColor: "#fef2f2" },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              minWidth: { xs: "92vw", sm: 420 },
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
            },
          }}
        >
          <DialogTitle
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: "error.dark",
              }}
            >
              <DeleteIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
              {t("Confirm Deletion")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: "background.default" }}>
            <DialogContentText sx={{ color: "text.primary", fontSize: 15 }}>
              {t("Are you sure you want to delete this complaint? This action cannot be undone.")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1 }}>
            <Button onClick={handleClose} variant="outlined" color="primary" sx={{ px: 2.5, borderRadius: 2 }}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ px: 2.5, borderRadius: 2 }}>
              {t("Delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  </>
);
}

export default AdminContactUs;
